import { useState, useCallback, useEffect } from "react";
import { createContainer } from "unstated-next";
import { Auth, useAuthedRequest } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";

export function useNotes({ iboNumber }) {
  const { makeAuthedRequest } = Auth.useContainer();
  const { logError } = useLogger({ prefix: "useNotes" });
  const [isWaiting, setIsWaiting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const [addFormIsOpen, setAddFormIsOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const [requestId, setRequestId] = useState(0);

  const {
    data: noteTypes,
    loading: loadingTypes,
    error: typesError,
  } = useAuthedRequest("/v1/membership/notes");

  // Fetches notes list initially and whenever requestId changes.
  useEffect(() => {
    if (requestId === 0 && notes != null) return;

    setIsWaiting(true);

    makeAuthedRequest(`/v1/membership/notes/${iboNumber}`)
      .then((res) => res.json())
      .then(setNotes)
      .catch(logError)
      .finally(() => {
        setIsWaiting(false);
      });
  }, [requestId]);

  // Increments the requestId, causing notes list to be refreshed.
  const refresh = useCallback(() => {
    setRequestId(requestId + 1);
  }, [requestId]);

  const addNote = useCallback(
    ({ value, type }) => {
      setIsAdding(true);

      return makeAuthedRequest(`/v1/membership/notes`, {
        method: "POST",
        body: JSON.stringify({
          iboNumber,
          note: value,
          type: type,
        }),
      })
        .then(async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const err = await res.json();
            throw new Error(err.message);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          // Close the form and refresh the notes list.
          setIsAdding(false);
          setAddFormIsOpen(false);
          refresh();
        });
    },
    [refresh]
  );

  return {
    notes,
    noteTypes,
    refresh,
    addNote,
    isWaiting: loadingTypes || isWaiting,
    isAdding,
    addFormIsOpen,
    setAddFormIsOpen,
  };
}

export const Notes = createContainer(useNotes);
